import React, { useEffect, useState } from "react";
import {
  TextField,
  Dialog
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getChapterById, updateChapter } from "../../State/Chapter/Action";
import { getModuleById } from "../../State/Module/Action";
import { getCourseById } from "../../State/Course/Action";
import { useSelector } from "react-redux";


const EditChapterInfo = () => {
  const navigate = useNavigate();
  const { chapterId } = useParams();
  const dispatch = useDispatch();
  
  const [chapterTitle, setChapterTitle] = useState("");
  const [chapterDescription, setChapterDescription] = useState("");
  const [previewAvailable, setPreviewAvailable] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [chapterData, setChapterData] = useState(null);
  const [moduleData, setModuleData] = useState(null);
  const [courseData, setCourseData] = useState(null);

  const { 
    chapterByIdResponse, 
    loading: chapterLoading, 
    error: chapterError,
    updateChapterResult,
    updateChapterLoading,
    updateChapterError
  } = useSelector((state) => state.chapter || {});
  const { moduleByIdResponse } = useSelector((state) => state.module || {});
  const { courseByIdResponse } = useSelector((state) => state.course || {});
  
  console.log("Chapter Redux State:", { chapterByIdResponse, chapterLoading, chapterError }); // Debug log

  useEffect(() => {
    if (chapterId) {
      console.log("EditChapterInfo mounted, fetching chapter with ID:", chapterId); // Debug log
      // Clear any previous chapter data to ensure fresh fetch
      setChapterData(null);
      setChapterTitle("");
      setChapterDescription("");
      // Fetch chapter data using Redux action
      dispatch(getChapterById(chapterId));
    } else {
      console.warn("No chapterId provided to EditChapterInfo"); // Debug log
    }
  }, [chapterId, dispatch]);

  useEffect(() => {
    if (chapterByIdResponse && Object.keys(chapterByIdResponse).length > 0) {
      console.log("Chapter Redux Response:", chapterByIdResponse); // Debug log
      console.log("Setting chapter data:", chapterByIdResponse); // Debug log
      
      setChapterData(chapterByIdResponse);
      setChapterTitle(chapterByIdResponse.title || "");
      setChapterDescription(chapterByIdResponse.description || "");
      setPreviewAvailable(chapterByIdResponse.previewAvailable || false);
      
      console.log("Form values set:", {
        title: chapterByIdResponse.title || "",
        description: chapterByIdResponse.description || ""
      }); // Debug log
      
      // Fetch module data
      if (chapterByIdResponse.moduleId) {
        console.log("Fetching module data for moduleId:", chapterByIdResponse.moduleId); // Debug log
        dispatch(getModuleById(chapterByIdResponse.moduleId));
      }
    }
  }, [chapterByIdResponse, dispatch]);

  useEffect(() => {
    if (chapterError) {
      console.error("Chapter loading error:", chapterError); // Debug log
      setErrorMessage(chapterError.message || "Failed to load chapter data");
      setErrorDialogOpen(true);
    }
  }, [chapterError]);

  useEffect(() => {
    if (moduleByIdResponse && moduleByIdResponse.module) {
      setModuleData(moduleByIdResponse.module);
      // Fetch course data
      if (moduleByIdResponse.module.courseId) {
        dispatch(getCourseById(moduleByIdResponse.module.courseId));
      }
    }
  }, [moduleByIdResponse, dispatch]);

  useEffect(() => {
    if (courseByIdResponse && courseByIdResponse.course) {
      setCourseData(courseByIdResponse.course);
    }
  }, [courseByIdResponse]);

  // Handle update chapter success
  useEffect(() => {
    if (updateChapterResult && Object.keys(updateChapterResult).length > 0) {
      // Navigate back to module list on successful update
      if (courseData && courseData.id) {
        navigate(`/module-list/${courseData.id}`);
      } else {
        navigate(-1);
      }
    }
  }, [updateChapterResult, courseData, navigate]);

  // Handle update chapter error
  useEffect(() => {
    if (updateChapterError) {
      setErrorMessage(updateChapterError.message || "Failed to update chapter. Please try again.");
      setErrorDialogOpen(true);
    }
  }, [updateChapterError]);

  const validateForm = () => {
    const newErrors = {};

    if (!chapterTitle || chapterTitle.length < 5 || chapterTitle.length > 128) {
      newErrors.chapterTitle =
        "Chapter title must be between 5 and 128 characters.";
    }

    if (
      !chapterDescription ||
      chapterDescription.length < 20 ||
      chapterDescription.length > 1000
    ) {
      newErrors.chapterDescription =
        "Description must be between 20 and 1000 characters.";
    }

    setErrors(newErrors);
    const errorMessages = Object.values(newErrors);
    if (errorMessages.length > 0) {
      setErrorMessage(errorMessages[0]);
      setErrorDialogOpen(true);
      return false;
    }
    return true;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    const updateData = {
      title: chapterTitle,
      description: chapterDescription,
      previewAvailable: previewAvailable,
    };

    // Use Redux action to update chapter
    dispatch(updateChapter(chapterId, updateData));
  };


  const handleCancel = () => {
    if (courseData && courseData.id) {
      navigate(`/module-list/${courseData.id}`);
    } else {
      navigate(-1);
    }
  };


  if (chapterLoading || (!chapterData && !chapterError)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading chapter data...</p>
          <p className="mt-2 text-sm text-gray-500">Chapter ID: {chapterId}</p>
        </div>
      </div>
    );
  }

  // Show error state if there's an error and no data
  if (chapterError && !chapterData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ErrorOutlineIcon sx={{ color: '#ef4444', fontSize: '2rem' }} />
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Failed to Load Chapter</h2>
          <p className="text-gray-600 mb-4">{chapterError.message || "An error occurred while loading the chapter data."}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Modern Header with Glassmorphism */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 opacity-90"></div>
        <div className="relative z-10 px-6 py-4">
          <div className="max-w-4xl mx-auto">
            {/* Back Button */}
            <button
              onClick={handleCancel}
              className="mb-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white hover:bg-white/30 transition-all duration-300"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="font-medium">Back</span>
            </button>

            {/* Breadcrumb */}
            {courseData && moduleData && (
              <div className="flex items-center gap-3 mb-3">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-md border border-white/30">
                  <span className="text-lg">🎓</span>
                  <span className="text-white font-medium text-sm">{courseData.courseName || courseData.name}</span>
                </div>
                <div className="w-1.5 h-1.5 rounded-full bg-white/60"></div>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-md border border-white/30">
                  <span className="text-lg">📚</span>
                  <span className="text-white font-medium text-sm">{moduleData.moduleTitle || moduleData.title}</span>
                </div>
              </div>
            )}

            {/* Page Title */}
            <div className="text-center">
              <h1 className="text-2xl font-bold text-white mb-1">Edit Chapter Info</h1>
              <p className="text-white/80 text-sm">Update your chapter title and description</p>
            </div>
          </div>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -translate-y-24 translate-x-24"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full translate-y-32 -translate-x-32"></div>
      </div>

      <div className="max-w-4xl mx-auto px-6 -mt-4 relative z-10">

        {/* Modern Form Card */}
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 mb-8">
          {/* Form Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r from-blue-500 to-purple-600 mb-4">
              <MenuBookIcon sx={{ color: "white", fontSize: "2rem" }} />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Chapter Information</h2>
            <p className="text-gray-600">Update your chapter title and description</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Chapter Title
              </label>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Enter chapter title"
                name="chapterTitle"
                value={chapterTitle}
                onChange={(e) => setChapterTitle(e.target.value)}
                error={Boolean(errors.chapterTitle)}
                helperText={errors.chapterTitle}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'white',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                    },
                    '& fieldset': {
                      borderColor: '#e5e7eb',
                      borderWidth: 2,
                    },
                    '&:hover fieldset': {
                      borderColor: '#3b82f6',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3b82f6',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#6b7280',
                    fontWeight: '500',
                  },
                  '& .MuiInputBase-input': {
                    padding: '16px 14px',
                    fontSize: '16px',
                  },
                }}
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Chapter Description
              </label>
              <TextField
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                placeholder="Describe what this chapter covers..."
                name="chapterDescription"
                value={chapterDescription}
                onChange={(e) => setChapterDescription(e.target.value)}
                error={Boolean(errors.chapterDescription)}
                helperText={errors.chapterDescription}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'white',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
                    },
                    '& fieldset': {
                      borderColor: '#e5e7eb',
                      borderWidth: 2,
                    },
                    '&:hover fieldset': {
                      borderColor: '#3b82f6',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3b82f6',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#6b7280',
                    fontWeight: '500',
                  },
                  '& .MuiInputBase-input': {
                    padding: '16px 14px',
                    fontSize: '16px',
                  },
                }}
              />
            </div>

            {/* Preview Available Checkbox */}
            <div className="mb-6">
              <div className="flex items-center gap-3 p-4 bg-white rounded-xl border-2 border-gray-200 hover:border-blue-300 transition-all duration-300">
                <input
                  type="checkbox"
                  id="previewAvailable"
                  checked={previewAvailable}
                  onChange={(e) => setPreviewAvailable(e.target.checked)}
                  className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <label 
                  htmlFor="previewAvailable"
                  className="text-gray-700 font-medium text-base cursor-pointer flex-1"
                >
                  👁️ Preview Available
                </label>
              </div>
              <p className="text-gray-500 text-sm mt-2 ml-1">
                Allow learners to preview this chapter before purchasing the course
              </p>
            </div>


            {/* Modern Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6">
              <button
                onClick={handleCancel}
                disabled={updateChapterLoading}
                className="flex-1 px-6 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-semibold hover:border-gray-400 hover:bg-gray-50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              
                     <button
                       type="submit"
                       disabled={updateChapterLoading}
                       className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                     >
                       {updateChapterLoading ? (
                         <div className="flex items-center justify-center gap-2">
                           <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                           Updating...
                         </div>
                       ) : (
                         "Update Chapter Info"
                       )}
                     </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modern Error Dialog */}
      <Dialog 
        open={errorDialogOpen} 
        onClose={() => setErrorDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            maxWidth: '400px',
          }
        }}
      >
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <ErrorOutlineIcon sx={{ color: '#ef4444', fontSize: '1.5rem' }} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Error</h3>
          </div>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <div className="flex justify-end">
            <button
              onClick={() => setErrorDialogOpen(false)}
              className="px-6 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors duration-200"
            >
              OK
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default EditChapterInfo;
